import { Decoder } from './decoder';
import { DecodingError } from './errors';
import { Result } from './result';

const okResult = <A>(value: A) => Result.ok(value);

const errorResult = <E>(error: E) => Result.error(error);

/** Creates a decoder instance from a function that decodes an unknown value. */
const createDecoder = Decoder.create;

/** Creates a decoder that always succeeds with the provided value. */
const pureDecoder = Decoder.pure;

/** Creates a decoder that always fails with the provided error. */
const failDecoder = Decoder.fail;

const typeMismatch = (expected: string, found: string) => Result.error(DecodingError.typeMismatch(expected, found));

/** A decoder that matches `undefined` values. */
const undefinedDecoder = Decoder.create((input) =>
    input === undefined ? Result.ok(input) : typeMismatch('undefined', typeof input),
);

/** A decoder that matches `null` values. */
const nullDecoder = Decoder.create((input) => (input === null ? Result.ok(input) : typeMismatch('null', typeof input)));

/** A decoder that matches anything (`unknown`). */
const anyDecoder: Decoder<unknown> = Decoder.create(Result.ok);

/** A decoder that matches `boolean` values. */
const booleanDecoder = Decoder.create((input) =>
    typeof input === 'boolean' ? Result.ok(input) : typeMismatch('boolean', typeof input),
);

/** A decoder that matches finite `number` values. */
const numberDecoder = Decoder.create((input) => {
    if (typeof input !== 'number') {
        return typeMismatch('number', typeof input);
    } else if (!Number.isFinite(input)) {
        return typeMismatch('number', input.toString());
    } else {
        return Result.ok(input);
    }
});

/** A decoder that matches integer `number` values. */
const integerDecoder = Decoder.create((input) => {
    if (typeof input !== 'number') {
        return typeMismatch('number', typeof input);
    } else if (!Number.isInteger(input)) {
        return typeMismatch('integer', input.toString());
    } else {
        return Result.ok(input);
    }
});

/** A decoder that matches `string` values. */
const stringDecoder = Decoder.create((input) =>
    typeof input === 'string' ? Result.ok(input) : typeMismatch('string', typeof input),
);

/** A decoder that matches homogeneous `Array` values. */
const arrayDecoder = <A>(decoder: Decoder<A>): Decoder<A[]> =>
    Decoder.create((input) => {
        if (!Array.isArray(input)) {
            return typeMismatch('array', typeof input);
        } else {
            const result: A[] = [] as A[];
            const errors: DecodingError[] = [];
            for (let i = 0; i < input.length; i++) {
                decoder.decode(input[i]).match(
                    (value) => result.push(value),
                    (newError) => errors.push(DecodingError.errorAtIndex(i, newError)),
                );
            }
            if (errors.length) {
                return Result.error(DecodingError.fold(errors));
            } else {
                return Result.ok(result);
            }
        }
    });

/** A decoder that matches a string-keyed map (homogenous JS object). */
const stringMapDecoder = <A>(decoder: Decoder<A>): Decoder<{ [key: string]: A }> =>
    Decoder.create((input) => {
        if (typeof input !== 'object') {
            return typeMismatch('object', typeof input);
        } else if (input === null) {
            return typeMismatch('object', 'null');
        } else {
            const object = input as { [key: string]: unknown };
            const result: { [key: string]: A } = {};
            const errors: DecodingError[] = [];
            for (const key in object) {
                decoder.decode(object[key]).match(
                    (value) => (result[key] = value),
                    (newError) => errors.push(DecodingError.errorAtProperty(key, newError)),
                );
            }
            if (errors.length) {
                return Result.error(DecodingError.fold(errors));
            } else {
                return Result.ok(result);
            }
        }
    });

/** A decoder that matches a record structure (hetereogenous JS object). */
const recordDecoder = <A>(decoders: { [key in keyof A]: Decoder<A[key]> }): Decoder<A> =>
    Decoder.create((input) => {
        if (typeof input !== 'object') {
            return typeMismatch('object', typeof input);
        } else if (input === null) {
            return typeMismatch('object', 'null');
        } else {
            const object = input as { [key: string]: unknown };
            const result = {} as A;
            const errors: DecodingError[] = [];
            for (const key in decoders) {
                decoders[key].decode(object[key]).match(
                    (value) => (result[key] = value),
                    (newError) => errors.push(DecodingError.errorAtProperty(key, newError)),
                );
            }
            if (errors.length) {
                return Result.error(DecodingError.fold(errors));
            } else {
                return Result.ok(result);
            }
        }
    });

export {
    Decoder,
    Result,
    DecodingError,
    okResult as ok,
    errorResult as error,
    createDecoder as create,
    pureDecoder as pure,
    failDecoder as fail,
    undefinedDecoder as undefined,
    nullDecoder as null,
    anyDecoder as any,
    booleanDecoder as boolean,
    numberDecoder as number,
    integerDecoder as integer,
    stringDecoder as string,
    arrayDecoder as array,
    stringMapDecoder as stringMap,
    recordDecoder as record,
};
